import { HTMLAttributes, useCallback, useMemo } from 'react';

import { Autocomplete, AutocompleteRenderInputParams } from '@mui/material';

import _isEqual from 'lodash/isEqual';
import styled from 'styled-components';

import { CountrySelectInput } from './CountrySelectInput';
import { Flag } from './Flag';

export interface CountrySelectOption {
  value?: string;
  label: string;
}

export interface CountrySelectProps {
  className: string;
  value?: string;
  onBlur: () => void;
  onChange: (value?: string) => void;
  options: CountrySelectOption[];
}

const StyledRenderOption = styled.li`
  padding: ${({ theme }) => theme.spacing(2)};

  .flag {
    padding-right: ${({ theme }) => theme.spacing(2)};
  }
`;

export const CountrySelect = ({ className, value, onBlur, onChange, options }: CountrySelectProps) => {
  const handleOnChange = useCallback(
    (event: React.SyntheticEvent, option: CountrySelectOption | null) => {
      if (option?.value) {
        onChange(option?.value);
      }
    },
    [onChange]
  );

  const optionsMap = useMemo(
    () => new Map(options.filter((option) => option.value != null).map((option) => [option.value, option])),
    [options]
  );

  const renderOption = useCallback(
    (props: HTMLAttributes<HTMLLIElement>, { value, label }: { value?: string; label: string }) => {
      return (
        <StyledRenderOption {...props}>
          <Flag value={value} className="flag" />
          <span className="label">{label}</span>
        </StyledRenderOption>
      );
    },
    []
  );

  const RenderedCountrySelectInput = (props: AutocompleteRenderInputParams) => (
    <CountrySelectInput value={value} {...props} />
  );

  return (
    <Autocomplete
      className={className}
      disableClearable
      isOptionEqualToValue={_isEqual}
      onBlur={onBlur}
      onChange={handleOnChange}
      options={options}
      renderInput={RenderedCountrySelectInput}
      renderOption={renderOption}
      value={optionsMap.get(value) ?? { label: 'International' }}
    />
  );
};
